import React, {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
    number, object, string, ObjectSchema, mixed,
} from 'yup';
import { useForm, Controller } from 'react-hook-form';
import { FILE_TYPES_ALLOWED } from 'pages/ClientOnboardingFlow/pages/NewsPlan/UploadDocumentsPage/validation/const';
import { ICON_TYPE_CLOSE_SLIM } from 'ui-library/components/Icon';
import {
    FileInput, Infobox, Loader, Modal, Select, TextInput, Icon,
} from 'ui-library';
import ServiceManager from '../../../../services/ServiceManager';

import { useLanguageSwitcher } from '../../../../translations/hooks/useLanguageSwitcher';
import { memberIdSelector } from '../../../../redux-store/auth/authSelectors';
import { useYupValidationResolver } from '../../../../hooks/useYupValidationResolver';
import { validateFile } from '../../../ClientOnboardingFlow/pages/NewsPlan/UploadDocumentsPage/validation/validation';
import { getBase64FromFile } from '../../../../utils/file';

import './UploadDocumentModal.css';

type TDocumentCategory = {
  id: string;
  label: string | string[];
};

type TProduct = {
  id: string;
  label: string;
};

type TUploadModalSchema = {
    documentCategoryId: string | null;
    documentTypeId: string | null;
    documentMetadataId: string | null;
    language: string | null;
    productId: number | null;
    version: string;
    file: File | undefined;
}

interface IUploadDocumentModalProps {
  onClose: () => void;
  documentCategories: TDocumentCategory[];
  productsList: TProduct[];
  getDocumentLists?: () => void;
  documentCategoryId?: string | null;
  isLoading?: boolean;
}

const defaultValues: TUploadModalSchema = {
    documentCategoryId: null,
    documentTypeId: null,
    documentMetadataId: null,
    language: null,
    productId: null,
    version: '',
    file: undefined,
};

const UploadDocumentModal = (props: IUploadDocumentModalProps) => {
    const { t } = useTranslation();
    const {
        onClose, documentCategories, productsList, getDocumentLists = () => null, documentCategoryId,
    } = props;

    const productListOptions = useMemo(() => productsList?.map(i => ({ label: i.label, value: String(i.id) })), [productsList]);
    const documentCategoriesOptions = useMemo(() => documentCategories?.map(i => ({ label: i.label, value: String(i.id) })), [documentCategories]);

    const memberId = useSelector(memberIdSelector);
    const [documentTypeOptions, setDocumentTypeOptions] = useState([]);
    const [inProgress, setInProgress] = useState(false);
    const [docTypesLoading, setDocTypesLoading] = useState(false);
    const [error, setError] = useState<string | undefined>(undefined);

    const { languages } = useLanguageSwitcher();

    const schema: ObjectSchema<TUploadModalSchema> = useMemo(() => object().shape({
        documentCategoryId: string().required(t('advisoryDashboard.docLibrary.requiredField')).nullable(),
        documentTypeId: string().required(t('advisoryDashboard.docLibrary.requiredField')).nullable(),
        language: string().required(t('advisoryDashboard.docLibrary.requiredField')).nullable(),
        productId: number().required(t('advisoryDashboard.docLibrary.requiredField')).nullable(),
        version: string().required(t('advisoryDashboard.docLibrary.requiredField')).nullable(),
        file: mixed<File>().required(t('advisoryDashboard.docLibrary.requiredField')).nullable(),
    }), [t]);

    const resolver = useYupValidationResolver(schema);

    const {
        control, handleSubmit, formState: { errors }, setValue, watch, reset,
    } = useForm({
        resolver,
        defaultValues,
    });

    const {
        file, version,
    } = watch();

    const getMetadata = useCallback(async () => {
        try {
            setDocTypesLoading(true);
            setValue('documentCategoryId', documentCategoryId ?? null);
            setValue('documentTypeId', null);
            setValue('documentMetadataId', null);

            const { data } = await ServiceManager.documentService('getDocumentMetaDataList', [{ documentCategoryId }]);

            setDocumentTypeOptions(data?.map(({ id, name }) => ({ label: [name], value: String(id) })) ?? []);
        } catch (err) {
            setDocumentTypeOptions([]);
        } finally {
            setDocTypesLoading(false);
        }
    }, [documentCategoryId]);

    useEffect(() => {
        (async () => getMetadata())();
    }, [getMetadata]);

    useEffect(() => {
        setError(undefined);
    }, [file, version]);

    const onModalClose = () => {
        setError(undefined);
        reset();
        onClose();
    };

    const onDocumentUpload = handleSubmit(async (values) => {
        try {
            setInProgress(true);

            const hasErrors = validateFile(file, {
                fileTypesAllowed: FILE_TYPES_ALLOWED,
            });

            if (hasErrors) {
                setError(hasErrors as string);

                return;
            }

            const payload = {
                filename: file?.[0]?.name,
                file: (await getBase64FromFile(file?.[0]))?.split(',')?.[1],
                documentMetadataId: parseInt(values.documentTypeId, 10),
                productId: parseInt(values.productId, 10),
                language: values.language,
                version: values.version,
            };

            await ServiceManager.customDocumentsService('uploadGeneralDocument', [{ payload, memberId }]);
            getDocumentLists();
            onModalClose();
        } catch (err: any) {
            setError((err?.message || '') as string);
        } finally {
            setInProgress(false);
        }
    });

    const closeIcon = useMemo(() => (<Icon type={ICON_TYPE_CLOSE_SLIM} size={20} />), []);

    return (
        <Modal
            keyboard={false}
            maskClosable={false}
            borderRadius
            closable
            closeIcon={closeIcon}
            title={t('advisoryDashboard.documents.uploadDocument')}
            cancelText={t('advisoryDashboard.documents.cancel')}
            onOk={onDocumentUpload}
            okText={inProgress ? <Loader /> : t('advisoryDashboard.documents.upload')}
            onCancel={onModalClose}
            width={450}
            visible
            className="UploadDocumentModal"
            okButtonProps={{ disabled: inProgress }}
        >
            <Controller
                name="documentCategoryId"
                control={control}
                render={({ field }) => (
                    <Select
                        {...field}
                        options={documentCategoriesOptions}
                        placeholder={t('clientDashboard.documents.category')}
                        error={errors?.documentCategoryId?.message}
                    />
                )}
            />

            <Controller
                name="documentTypeId"
                control={control}
                render={({ field }) => (
                    <Select
                        {...field}
                        disabled={docTypesLoading || !documentCategoryId || !documentTypeOptions?.length}
                        options={documentTypeOptions}
                        placeholder={t('clientDashboard.documents.type')}
                        error={errors?.documentTypeId?.message}
                    />
                )}
            />

            <Controller
                name="language"
                control={control}
                render={({ field }) => (
                    <Select
                        {...field}
                        innerRef={field.ref}
                        options={languages}
                        placeholder={t('common.selectLanguage')}
                        error={errors?.language?.message}
                    />
                )}
            />
            <Controller
                name="productId"
                control={control}
                render={({ field }) => (
                    <Select
                        {...field}
                        placeholder={t('advisoryDashboard.documents.product')}
                        error={errors?.productId?.message}
                        options={productListOptions}
                    />
                )}
            />

            <Controller
                name="version"
                control={control}
                render={({ field }) => (
                    <TextInput
                        {...field}
                        placeholder={t('advisoryDashboard.documents.version')}
                        error={errors?.version?.message}
                    />
                )}
            />

            <div className="uploadFile">
                <Controller
                    name="file"
                    control={control}
                    render={({ field: { onChange, ref } }) => (
                        <FileInput
                            ref={ref}
                            onChange={onChange}
                            className="uploadFile"
                            accept=".pdf, .jpg, .jpeg, .png"
                            uploadText={t('advisoryDashboard.documents.selectFile')}
                            error={errors?.file?.message}
                        />
                    )}
                />
            </div>

            {!!error && <Infobox error>{error}</Infobox>}
        </Modal>
    );
};

export default UploadDocumentModal;
